import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Card, Grid } from '@mui/material';
import debounce from 'lodash/debounce';

import MDBox from 'components/MDBox';
import DataTable from 'examples/Tables/DataTable';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import AddPolicy from './components/AddPolicy';
import ActionsPopover from '../../components/ActionsPopover/ActionsPopover';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import PreviewPolicy from './components/PreviewPolicy';

import {
  addPolicy,
  deletePolicy,
  getPolicyList,
  resetPolicies,
  updatePolicy,
} from '../../store/reducers/legalSlice';
import { legalColumns } from 'constants/columns.constants';

const Legal = () => {
  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const { policyList } = useSelector((state) => state.legal);

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isEditPolicy, setEditPolicy] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [isPreviewModal, setPreviewModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const columns = useMemo(() => {
    return legalColumns;
  }, []);

  const rows =
    policyList?.policyData?.length > 0
      ? policyList?.policyData?.map((policy) => {
          return {
            id: policy?.id,
            title: policy?.title ?? 'NA',
            content: (
              <div
                dangerouslySetInnerHTML={{ __html: policy?.content ?? '' }}
              ></div>
            ),
            actions: (
              <ActionsPopover
                viewTitle={'View Policy'}
                editTitle={'Edit Policy'}
                // deleteTitle={'Delete Policy'}
                onEditClick={() => {
                  setSelectedPolicy(policy);
                  setEditPolicy(true);
                  setOpenModal(true);
                }}
                onViewClick={() => {
                  setSelectedPolicy(policy);
                  setPreviewModal(true);
                }}
                // onDeleteClick={() => {
                //   setSelectedPolicy(policy);
                //   setDeleteModal(true);
                // }}
              />
            ),
          };
        })
      : [];

  async function fetchPolicyList() {
    try {
      setLoading(true);
      await dispatch(
        getPolicyList({
          page: currentPage + 1,
          limit: perPage,
          sortField: sortBy?.id ?? 'id',
          sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
          search: searchTerm,
        })
      );
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onCloseModal = () => {
    setSelectedPolicy(null);
    setEditPolicy(false);
    setOpenModal(false);
    setDeleteModal(false);
    setPreviewModal(false);
  };

  const onCreatePolicy = async (policyData) => {
    try {
      await dispatch(
        addPolicy({
          ...policyData,
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchPolicyList();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const onUpdatePolicy = async (policyData) => {
    try {
      await dispatch(
        updatePolicy({
          ...policyData,
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchPolicyList();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const onDeletePolicy = async () => {
    try {
      await dispatch(deletePolicy(selectedPolicy?.id));
      if (currentPage === 0) {
        fetchPolicyList();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      onCloseModal();
    }
  };

  const onFetchDataDebounced = debounce(fetchPolicyList, 0);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, searchTerm, perPage]);

  useEffect(() => {
    return () => {
      dispatch(resetPolicies());
    };
  }, []);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Legal Policies
              </MDTypography>
              <MDButton
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenModal(true)}
              >
                Add Policy
              </MDButton>
            </MDBox>
            <MDBox
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={false}
                showTotalEntries
                canSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={policyList?.totalPage}
                totalRecords={policyList?.totalCount}
                hiddenColumns={['id']}
                queryPageSortBy={[sortBy]}
                setSortBy={setSortBy}
                isLoading={isLoading}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
      {openModal && (
        <AddPolicy
          openModal={openModal}
          onCloseModal={onCloseModal}
          onCreatePolicy={onCreatePolicy}
          onUpdatePolicy={onUpdatePolicy}
          isEdit={isEditPolicy}
          selectedPolicy={selectedPolicy}
        />
      )}
      {/* {isDeleteModal && (
        <ConfirmModal
          open={isDeleteModal}
          onCloseModal={onCloseModal}
          modalText="Are you sure you want to delete this policy?"
          okayText="Yes, delete"
          cancelText="No, cancel"
          onOkayClick={onDeletePolicy}
        />
      )} */}
      {isPreviewModal && (
        <PreviewPolicy
          openModal={isPreviewModal}
          onCloseModal={onCloseModal}
          selectedPolicy={selectedPolicy}
        />
      )}
    </MDBox>
  );
};

export default Legal;
